import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, Pane } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import "leaflet/dist/leaflet.css";
import { MapPin, Building } from "lucide-react";
import districtBoundary from "../../boundary/AndhraPradesh_Districts.json";
import stateBoundary from "../../boundary/andhra_pradesh.json";

const getPinIcon = (isOffline: boolean) => {
    const colorClass = isOffline ? "text-red-500" : "text-green-500";
    const shadowColor = isOffline ? "rgba(239, 68, 68, 0.4)" : "rgba(34, 197, 94, 0.4)";

    return L.divIcon({
        html: ReactDOMServer.renderToString(
            <div className="flex items-center justify-center">
                <div className="relative flex items-center justify-center">
                    <MapPin className={`h-6 w-6 ${colorClass} drop-shadow-[0_2px_4px_${shadowColor}]`} fill="currentColor" />
                    <div className="absolute top-[4px] flex items-center justify-center">
                        <Building className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    </div>
                </div>
            </div>
        ),
        className: "custom-client-marker",
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -20],
    });
};

interface MiniMapProps {
    clients: any[];
    selectedDistrictName?: string;
}

export const MiniMap: React.FC<MiniMapProps> = ({ clients, selectedDistrictName }) => {
    console.log("client_data ids", clients)
    const navigate = useNavigate();
    const apCenter: [number, number] = [15.9129, 79.7400];

    const filteredFeatures = useMemo(() => {
        if (!selectedDistrictName) return (districtBoundary as any).features;
        return (districtBoundary as any).features.filter((f: any) =>
            f.properties?.district_name?.toLowerCase() === selectedDistrictName.toLowerCase()
        );
    }, [selectedDistrictName]);

    const worldMaskGeoJSON = useMemo(() => {
        try {
            let geometryToExclude: any = null;

            if (selectedDistrictName && filteredFeatures.length > 0) {
                geometryToExclude = filteredFeatures[0]?.geometry;
            } else {
                geometryToExclude = (stateBoundary as any)[0]?.geojson;
            }

            if (!geometryToExclude) return null;

            const worldRing = [[-1000, -90], [1000, -90], [1000, 90], [-1000, 90], [-1000, -90]];
            const holes: any[] = [];
            if (geometryToExclude.type === 'Polygon') holes.push(geometryToExclude.coordinates[0]);
            else if (geometryToExclude.type === 'MultiPolygon') geometryToExclude.coordinates.forEach((p: any) => holes.push(p[0]));
            return {
                type: "Feature",
                properties: {},
                geometry: { type: "Polygon", coordinates: [worldRing, ...holes] }
            };
        } catch (e) { return null; }
    }, [selectedDistrictName, filteredFeatures]);

    const apDistricts = useMemo(() => ({
        type: "FeatureCollection",
        features: filteredFeatures
    } as any), [filteredFeatures]);

    const bounds = useMemo(() => {
        if (selectedDistrictName && filteredFeatures.length > 0) {
            try {
                const layer = L.geoJSON({
                    type: "FeatureCollection",
                    features: filteredFeatures
                } as any);
                return layer.getBounds();
            } catch (e) {
                return null;
            }
        }
        return null;
    }, [selectedDistrictName, filteredFeatures]);

    return (
        <div className="h-full rounded-xl overflow-hidden border border-slate-200 bg-slate-105 relative z-0">
            <MapContainer
                key={selectedDistrictName || 'global'}
                center={bounds ? undefined : apCenter}
                zoom={bounds ? undefined : 5.8}
                bounds={bounds || undefined}
                scrollWheelZoom={true}
                zoomControl={false}
                style={{ width: "100%", height: "100%" }}
                className="z-0"
                minZoom={5.8}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                <Pane name="mask-pane" style={{ zIndex: 400 }}>
                    {worldMaskGeoJSON && (
                        <GeoJSON
                            data={worldMaskGeoJSON as any}
                            interactive={false}
                            style={{
                                color: "#ffffff",
                                weight: 0,
                                fillColor: "#ffffff",
                                fillOpacity: 1,
                            }}
                        />
                    )}
                </Pane>

                <Pane name="districts-pane" style={{ zIndex: 450 }}>
                    <GeoJSON
                        data={apDistricts as any}
                        onEachFeature={(feature, layer) => {
                            if (feature.properties?.district_name) {
                                layer.bindTooltip(feature.properties.district_name, {
                                    sticky: true,
                                    className: 'custom-district-tooltip'
                                });
                            }

                            layer.on({
                                mouseover: (e: any) => {
                                    e.target.setStyle({
                                        color: "#422568",
                                        weight: 2,
                                        fillColor: "#5b3889ff",
                                        fillOpacity: 0.2
                                    });
                                    e.target.bringToFront();
                                },
                                mouseout: (e: any) => {
                                    e.target.setStyle({
                                        color: "#475569",
                                        weight: 0.5,
                                        fillOpacity: 0
                                    });
                                }
                            });
                        }}
                        style={{
                            color: "#475569",
                            weight: 0.5,
                            fillOpacity: 0
                        }}
                    />

                    <GeoJSON
                        data={stateBoundary as any}
                        interactive={false}
                        style={{
                            color: "#ea580c",
                            weight: 1.5,
                            fillOpacity: 0
                        }}
                    />
                </Pane>

                <style>{`
                    .custom-district-tooltip {
                        background: white !important;
                        border: 1px solid #e2e8f0 !important;
                        border-radius: 6px !important;
                        padding: 2px 6px !important;
                        font-size: 9px !important;
                        font-weight: 900 !important;
                        text-transform: uppercase !important;
                        color: #1e293b !important;
                        box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
                    }
                `}</style>

                {clients.map((item) => (
                    <Marker key={item.id} position={item.coordinate} icon={getPinIcon(item.is_offline)}>
                        <Popup>
                            <div className="p-1 min-w-[100px]">
                                <p className="text-[10px] font-black text-slate-800 border-b border-slate-100 pb-1 mb-1.5 uppercase">{item.display_name}</p>
                                <button
                                    onClick={() => navigate(`/client-details?client_id=${item.id}&path=dashboard`)}
                                    className="w-full bg-orange-600 text-[8px] font-black text-white py-1.5 rounded-lg shadow-md shadow-orange-500/20 hover:bg-orange-700 transition-all uppercase tracking-widest"
                                >
                                    Details
                                </button>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};
